import React, { useState } from 'react';
import {
  Card,
  Button,
  Form,
  Alert,
  Badge,
  Collapse,
  Spinner,
  ProgressBar,
} from 'react-bootstrap';
import { post } from '../../../Services/API';
import EmailInboxPanel from '../../../Components/EmailInboxPanel';

/**
 * AI Extract Panel Component
 * Allows users to paste text from partner portals and extract booking data using AI
 * Only shown for new bookings (not when editing)
 * Note: Flight validation happens in OrderInputForm after data is applied
 */
function AIExtractPanel({ onApplyExtraction }) {
  const [isOpen, setIsOpen] = useState(false);
  const [text, setText] = useState('');
  const [showInbox, setShowInbox] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const handleExtract = async () => {
    if (!text || text.length < 20) {
      setError('Please paste more text to extract booking details');
      return;
    }

    setLoading(true);
    setError('');
    setResult(null);

    try {
      const response = await post('bookings/ai-extract', { text });
      const data = response?.data?.data;

      if (data?.success) {
        setResult(data);
      } else {
        setError('Failed to extract booking details. Please try again.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to extract booking details');
    } finally {
      setLoading(false);
    }
  };

  const handleApply = () => {
    if (result?.booking) {
      onApplyExtraction(result.booking);
      setIsOpen(false);
      setText('');
      setResult(null);
    }
  };

  const getConfidenceVariant = (confidence) => {
    if (confidence >= 0.8) return 'success';
    if (confidence >= 0.5) return 'warning';
    return 'danger';
  };

  const getConfidenceLabel = (confidence) => {
    if (confidence >= 0.8) return 'High';
    if (confidence >= 0.5) return 'Medium';
    return 'Low';
  };

  return (
    <Card className="mb-3 ai-extract-panel">
      <Card.Header
        onClick={() => setIsOpen(!isOpen)}
        className="ai-panel-header d-flex justify-content-between align-items-center"
      >
        <div className="d-flex align-items-center gap-2 text-white">
          <span className="ai-icon">🤖</span>
          <span className="ai-title">AI Booking Extractor</span>
        </div>
        <Badge className="ai-badge text-white" pill>
          {isOpen ? '▲ Collapse' : '▼ Expand'}
        </Badge>
      </Card.Header>
      <Collapse in={isOpen}>
        <Card.Body className="ai-panel-body">
          <Form.Group className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <Form.Label className="fw-semibold text-dark mb-0">
                <i className="fa fa-paste me-2 text-primary" />
                Paste booking text from partner portal
              </Form.Label>
              <div className="btn-group btn-group-sm">
                <Button
                  variant={!showInbox ? 'primary' : 'outline-secondary'}
                  size="sm"
                  onClick={() => setShowInbox(false)}
                >
                  <i className="fa fa-keyboard-o me-1" />
                  Paste text
                </Button>
                <Button
                  variant={showInbox ? 'primary' : 'outline-secondary'}
                  size="sm"
                  onClick={() => setShowInbox(true)}
                >
                  <i className="fa fa-inbox me-1" />
                  From inbox
                </Button>
              </div>
            </div>
            {showInbox ? (
              <EmailInboxPanel
                onSelect={(html) => {
                  setText(html);
                  setShowInbox(false);
                }}
              />
            ) : (
              <>
                <Form.Control
                  as="textarea"
                  rows={6}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Paste booking details from KiwiTaxi, GetTransfer, or any partner portal..."
                  disabled={loading}
                  className="ai-textarea"
                />
                <div className="ai-help-text mt-2">
                  <i className="fa fa-info-circle" />
                  <span>
                    AI will automatically extract pickup/dropoff locations,
                    dates, passenger info, and pricing
                  </span>
                </div>
              </>
            )}
          </Form.Group>

          {error && (
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              <i className="fa fa-exclamation-circle me-2" />
              {error}
            </Alert>
          )}

          <div className="d-flex gap-2 mb-3">
            <Button
              className="ai-extract-btn"
              onClick={handleExtract}
              disabled={loading || !text}
            >
              {loading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Analyzing with AI...
                </>
              ) : (
                <>
                  <span className="magic-icon me-2">✨</span>
                  Extract with AI
                </>
              )}
            </Button>
            <Button
              variant="outline-secondary"
              onClick={() => {
                setText('');
                setResult(null);
                setError('');
              }}
              disabled={loading}
              style={{ borderRadius: '10px' }}
            >
              <i className="fa fa-times me-2" />
              Clear
            </Button>
          </div>

          {result && (
            <div className="ai-result-card">
              <div className="ai-result-header d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center gap-2">
                  <span style={{ fontSize: '1.25rem' }}>🎯</span>
                  <span className="fw-semibold">Extraction Results</span>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted small">Confidence:</span>
                  <Badge
                    bg={getConfidenceVariant(result.confidence)}
                    className="confidence-badge"
                  >
                    {getConfidenceLabel(result.confidence)}
                    {' '}
                    (
                    {Math.round(result.confidence * 100)}
                    %)
                  </Badge>
                </div>
              </div>
              <div className="ai-result-body">
                <ProgressBar
                  now={result.confidence * 100}
                  variant={getConfidenceVariant(result.confidence)}
                  className="confidence-bar mb-3"
                />

                {result.warnings && result.warnings.length > 0 && (
                  <Alert variant="warning" className="mb-3">
                    <i className="fa fa-exclamation-triangle me-2" />
                    <strong>Warnings:</strong>
                    <ul className="mb-0 mt-2">
                      {result.warnings.map((warning) => (
                        <li key={warning}>{warning}</li>
                      ))}
                    </ul>
                  </Alert>
                )}

                <div className="row">
                  {result.booking.full_name && (
                    <div className="col-md-6">
                      <div className="extracted-field">
                        <div className="field-label">
                          <i className="fa fa-user me-1" />
                          Customer Name
                        </div>
                        <div className="field-value">{result.booking.full_name}</div>
                      </div>
                    </div>
                  )}
                  {result.booking.phone_number && (
                    <div className="col-md-6">
                      <div className="extracted-field">
                        <div className="field-label">
                          <i className="fa fa-phone me-1" />
                          Phone Number
                        </div>
                        <div className="field-value">{result.booking.phone_number}</div>
                      </div>
                    </div>
                  )}
                  {result.booking.email_address && (
                    <div className="col-md-6">
                      <div className="extracted-field">
                        <div className="field-label">
                          <i className="fa fa-envelope me-1" />
                          Email
                        </div>
                        <div className="field-value">{result.booking.email_address}</div>
                      </div>
                    </div>
                  )}
                  {result.booking.destination_from?.address && (
                    <div className="col-12">
                      <div className="extracted-field">
                        <div className="field-label">
                          <i className="fa fa-map-marker me-1" />
                          Pickup Location
                        </div>
                        <div className="field-value">
                          {result.booking.destination_from.address}
                        </div>
                      </div>
                    </div>
                  )}
                  {result.booking.destination_to?.address && (
                    <div className="col-12">
                      <div className="extracted-field">
                        <div className="field-label">
                          <i className="fa fa-flag-checkered me-1" />
                          Dropoff Location
                        </div>
                        <div className="field-value">
                          {result.booking.destination_to.address}
                        </div>
                      </div>
                    </div>
                  )}
                  {result.booking.date && (
                    <div className="col-md-6">
                      <div className="extracted-field">
                        <div className="field-label">
                          <i className="fa fa-calendar me-1" />
                          Date & Time
                        </div>
                        <div className="field-value">{result.booking.date}</div>
                      </div>
                    </div>
                  )}
                  {result.booking.passengers?.amount && (
                    <div className="col-md-6">
                      <div className="extracted-field">
                        <div className="field-label">
                          <i className="fa fa-users me-1" />
                          Passengers
                        </div>
                        <div className="field-value">
                          {result.booking.passengers.amount}
                        </div>
                      </div>
                    </div>
                  )}
                  {result.booking.category?.name && (
                    <div className="col-md-6">
                      <div className="extracted-field">
                        <div className="field-label">
                          <i className="fa fa-car me-1" />
                          Vehicle Type
                        </div>
                        <div className="field-value">{result.booking.category.name}</div>
                      </div>
                    </div>
                  )}
                  {result.booking.total_price > 0 && (
                    <div className="col-md-6">
                      <div className="extracted-field">
                        <div className="field-label">
                          <i className="fa fa-money me-1" />
                          Price
                        </div>
                        <div className="field-value">
                          {result.booking.total_price}
                          {' '}
                          {result.booking.currency}
                        </div>
                      </div>
                    </div>
                  )}
                  {result.booking.payment?.type && (
                    <div className="col-md-6">
                      <div className="extracted-field">
                        <div className="field-label">
                          <i className="fa fa-credit-card me-1" />
                          Payment Type
                        </div>
                        <div className="field-value">{result.booking.payment.type}</div>
                      </div>
                    </div>
                  )}
                  {result.booking.operator_cash_from_client > 0 && (
                    <div className="col-md-6">
                      <div className="extracted-field">
                        <div className="field-label">
                          <i className="fa fa-money-bill me-1" />
                          Cash from Client
                        </div>
                        <div className="field-value">
                          {result.booking.operator_cash_from_client}
                          {' '}
                          {result.booking.operator_cash_from_client_currency}
                        </div>
                      </div>
                    </div>
                  )}
                  {result.booking.partner_booking_reference && (
                    <div className="col-md-6">
                      <div className="extracted-field">
                        <div className="field-label">
                          <i className="fa fa-hashtag me-1" />
                          Reference
                        </div>
                        <div className="field-value">
                          {result.booking.partner_booking_reference}
                        </div>
                      </div>
                    </div>
                  )}
                  {result.booking.flight_number && (
                    <div className="col-md-6">
                      <div className="extracted-field">
                        <div className="field-label">
                          <i className="fa fa-plane me-1" />
                          Flight Number
                        </div>
                        <div className="field-value">
                          {result.booking.flight_number}
                        </div>
                      </div>
                    </div>
                  )}
                  {(result.booking.passengers?.infant_carrier > 0
                    || result.booking.passengers?.convertible_seat > 0
                    || result.booking.passengers?.booster_seat > 0) && (
                    <div className="col-12">
                      <div className="extracted-field">
                        <div className="field-label">
                          <i className="fa fa-child me-1" />
                          Child Seats
                        </div>
                        <div className="field-value">
                          {result.booking.passengers.infant_carrier > 0
                            && `Infant carriers: ${result.booking.passengers.infant_carrier}`}
                          {result.booking.passengers.convertible_seat > 0
                            && `${result.booking.passengers.infant_carrier > 0 ? ', ' : ''}Child seats: ${result.booking.passengers.convertible_seat}`}
                          {result.booking.passengers.booster_seat > 0
                            && `${(result.booking.passengers.infant_carrier > 0 || result.booking.passengers.convertible_seat > 0) ? ', ' : ''}Booster seats: ${result.booking.passengers.booster_seat}`}
                        </div>
                      </div>
                    </div>
                  )}
                  {result.booking.category?.capacity > 0 && (
                    <div className="col-md-6">
                      <div className="extracted-field">
                        <div className="field-label">
                          <i className="fa fa-users me-1" />
                          Vehicle Capacity
                        </div>
                        <div className="field-value">
                          {result.booking.category.capacity}
                          {' passengers'}
                        </div>
                      </div>
                    </div>
                  )}
                  {result.booking.category?.max_luggage > 0 && (
                    <div className="col-md-6">
                      <div className="extracted-field">
                        <div className="field-label">
                          <i className="fa fa-suitcase me-1" />
                          Max Luggage
                        </div>
                        <div className="field-value">
                          {result.booking.category.max_luggage}
                          {' bags'}
                        </div>
                      </div>
                    </div>
                  )}
                  {result.booking.comments && (
                    <div className="col-12">
                      <div className="extracted-field">
                        <div className="field-label">
                          <i className="fa fa-comment me-1" />
                          Additional Notes
                        </div>
                        <div className="field-value" style={{ whiteSpace: 'pre-line' }}>
                          {result.booking.comments}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-3 pt-3 border-top">
                  <Button className="ai-apply-btn" onClick={handleApply}>
                    <i className="fa fa-check-circle me-2" />
                    Apply to Booking Form
                  </Button>
                  <div className="text-muted small mt-2">
                    <i className="fa fa-info-circle me-1" />
                    All extracted data will be applied to the booking form
                  </div>
                </div>
              </div>
            </div>
          )}
        </Card.Body>
      </Collapse>
    </Card>
  );
}

export default AIExtractPanel;
